import { atom, useAtom, useStore } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { useForm } from 'react-hook-form';
import { IKid, IAddKidRequest, IEditKidRequest } from '@/shared/interfaces';
import { Utils } from '@/shared/Utils';
import {
    ChildModalMode,
    ChildModalProps,
    IFormKid,
    PrefilledChild,
    PARENTAL_AUTHORITY_CONFIRMATION_FAILED,
} from '@/features/shared/features/addOrEditChild/model';
import { useAddOrEditChild } from '@/features/shared/features/addOrEditChild/lib';

const hasShownTermsAtom = atom<boolean>();
const isEditingAtom = atom<boolean>();
const showModulusWarningAtom = atom(false);
const showParentalAuthorityWarningAtom = atom(false);

export const useChildForm = ({
    mode,
    kid,
    prefilledChild,
    onMutationSuccess,
    onClose,
    skipCloseAfterSuccess,
}: ChildModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors, dirtyFields },
        getValues,
    } = useForm<IFormKid>({
        defaultValues: createDefaultFormValues(mode, kid, prefilledChild),
        mode: 'onSubmit',
    });

    const store = useStore();

    useHydrateAtoms([
        [hasShownTermsAtom, mode.isEditing],
        [isEditingAtom, mode.isEditing],
    ]);

    const [showModulusWarning, setShowModulusWarning] = useAtom(
        showModulusWarningAtom,
    );
    const [showParentalAuthorityWarning, setShowParentalAuthorityWarning] =
        useAtom(showParentalAuthorityWarningAtom);

    const { mutate, status, reset, errorMessage } = useAddOrEditChild({
        mode,
    });

    const isProcessing = status === 'pending';

    // Show error only if in editing mode, parental authority is confirmed,
    // and CPR field hasn't been touched by user yet
    const shouldShowCprError =
        mode.isEditing &&
        kid?.IsParentalAuthorityConfirmed === false &&
        !dirtyFields.CPR;

    const onSuccess = async () => {
        try {
            onMutationSuccess?.();
        } finally {
            hideModulusWarning();
            hideParentalAuthorityWarning();
            if (!skipCloseAfterSuccess) onClose();
        }
        reset();
    };

    const onError = (error: Error) => {
        hideModulusWarning();

        if (error.message.includes(PARENTAL_AUTHORITY_CONFIRMATION_FAILED)) {
            setShowParentalAuthorityWarning(true);
            return;
        }
    };

    const onSubmit = () => {
        if (status === 'pending') return;

        const showModulusWarning = store.get(showModulusWarningAtom);

        const data = getValues();

        if (
            !prefilledChild &&
            !Utils.cprModulusCheck(data.CPR) &&
            !showModulusWarning
        ) {
            setShowModulusWarning(true);
            return;
        }

        if (mode.isAdding) {
            const addData: IAddKidRequest = {
                Name: data.Name,
                CPR: data.CPR,
                ShouldCheckParentalAuthority: true,
            };
            mutate(addData, { onSuccess, onError });
        } else {
            const editData: IEditKidRequest = {
                UID: data.UID!,
                Name: data.Name,
                CPR: data.CPR,
                ShouldCheckParentalAuthority: true,
            };
            mutate(editData, { onSuccess, onError });
        }
    };

    const onSubmitWithoutParentalCheck = () => {
        if (status === 'pending') return;

        const data = getValues();

        if (mode.isAdding) {
            const addData: IAddKidRequest = {
                Name: data.Name,
                CPR: data.CPR,
                ShouldCheckParentalAuthority: false,
            };
            mutate(addData, { onSuccess, onError });
        } else {
            const editData: IEditKidRequest = {
                UID: data.UID!,
                Name: data.Name,
                CPR: data.CPR,
                ShouldCheckParentalAuthority: false,
            };
            mutate(editData, { onSuccess, onError });
        }
    };

    const hideModulusWarning = () => setShowModulusWarning(false);
    const hideParentalAuthorityWarning = () => {
        reset();
        setShowParentalAuthorityWarning(false);
    };

    return {
        register,
        handleSubmit,
        errors,
        onSubmit,
        onSubmitWithoutParentalCheck,
        status,
        isProcessing,
        reset,
        errorMessage,
        showModulusWarning,
        hideModulusWarning,
        showParentalAuthorityWarning,
        hideParentalAuthorityWarning,
        getValues,
        shouldShowCprError,
    };
};

const createDefaultFormValues = (
    mode: ChildModalMode,
    kid?: IKid,
    prefilledChild?: PrefilledChild,
): IFormKid => {
    if (mode.isEditing && kid) {
        return (({ FormattedCpr, ...rest }) => ({
            ...rest,
            ShouldCheckParentalAuthority: true,
        }))(kid);
    }

    if (mode.isAdding) {
        return {
            Name: prefilledChild?.name ?? '',
            CPR: prefilledChild?.cpr ?? '',
            ShouldCheckParentalAuthority: true,
        };
    }

    return undefined;
};
